/**
 * SAP Overall Block (AUFSD) codes from KNA1 (`customers.central_order_block`).
 *
 * Authored as name -> code so call sites read `OVERALL_BLOCKS.inactive` instead of the opaque `'1'`
 * magic string. SAP sends AUFSD as a zero-padded 2-char code (e.g. `'01'`), and the customer import
 * parses it with `sapStringSchema` (whitespace-only trim), so what we persist verbatim is `'01'` —
 * NOT `'1'`. Matching normalizes leading zeros on the input, so either form (`'1'` or `'01'`)
 * resolves; raw SQL comparisons against the stored column must match both forms.
 *
 * The name is the developer-facing handle and doubles as the i18n key (translate it under the
 * `overallBlock` block). The UI labels the column "Overall Block" and answers Yes/No, so `inactive`
 * translates to "Yes" / "Oui" and `active` to "No" / "Non" — the names stay as they are because they
 * are also the filter's query values. For display we surface the canonical zero-padded SAP code via
 * `OVERALL_BLOCK_DISPLAY_CODES`, so the UI reads "Yes (01)".
 *
 * `'1'` (SAP `'01'`) marks a closed / inactive account.
 */
export const OVERALL_BLOCKS = {
  inactive: '1',
} as const

export type OverallBlockName = keyof typeof OVERALL_BLOCKS
export type OverallBlockCode = (typeof OVERALL_BLOCKS)[OverallBlockName]

export const OVERALL_BLOCK_NAMES = Object.keys(OVERALL_BLOCKS) as OverallBlockName[]
export const OVERALL_BLOCK_CODES = Object.values(OVERALL_BLOCKS) as OverallBlockCode[]

/**
 * Overall Block toggle statuses used by the customers filter: "inactive" matches the inactive
 * (closed) code (`'1'`, SAP `'01'`) and shows as "Yes", "active" is the catch-all for everything else
 * (no block or any other code) and shows as "No". Shared by the frontend filter hook and the backend
 * WHERE clause. Ordered alphabetically — the filter renders them in this order.
 */
export const OVERALL_BLOCK_STATUSES = ['active', 'inactive'] as const
export type OverallBlockStatus = (typeof OVERALL_BLOCK_STATUSES)[number]

/** Canonical zero-padded SAP code shown in the UI, e.g. "Yes (01)". */
export const OVERALL_BLOCK_DISPLAY_CODES: Record<OverallBlockName, string> = {
  inactive: '01',
}

export const OVERALL_BLOCK_NAME_BY_CODE = Object.fromEntries(
  Object.entries(OVERALL_BLOCKS).map(([name, code]) => [code, name]),
) as Record<OverallBlockCode, OverallBlockName>

/** Strip leading whitespace/zeros so `'01'` and `'1'` compare equal (mirrors `trimLeadingZerosSchema`). */
function normalizeOverallBlockCode(value: string): string {
  return value.replace(/^[\s0]+/, '')
}

export function isOverallBlockCode(value: string | null | undefined): value is OverallBlockCode {
  if (value == null) return false
  return (
    OVERALL_BLOCK_NAME_BY_CODE[normalizeOverallBlockCode(value) as OverallBlockCode] !== undefined
  )
}

export function getOverallBlockName(code: string): OverallBlockName | string {
  return OVERALL_BLOCK_NAME_BY_CODE[normalizeOverallBlockCode(code) as OverallBlockCode] ?? code
}

/** The canonical zero-padded code for display (e.g. `'01'`), falling back to the raw code if unknown. */
export function getOverallBlockDisplayCode(code: string): string {
  const name = OVERALL_BLOCK_NAME_BY_CODE[normalizeOverallBlockCode(code) as OverallBlockCode]
  return name ? OVERALL_BLOCK_DISPLAY_CODES[name] : code
}

/** An account is inactive when its Overall Block code is the "inactive" code (`'1'`, SAP `'01'`). */
export function isInactiveOverallBlock(code: string | null | undefined): boolean {
  return code != null && normalizeOverallBlockCode(code) === OVERALL_BLOCKS.inactive
}

/**
 * The Overall Block status the UI shows for an Overall Block code. Only the inactive code (`'1'`, SAP
 * `'01'`) is inactive ("Yes"); an absent code — the common case, since SAP only sets AUFSD on a block
 * — and any other code are active ("No"). Mirrors the `OVERALL_BLOCK_STATUSES` filter's catch-all
 * semantics, so the "Overall Block" column/detail row never renders a blank for an unblocked account.
 */
export function getAccountStatus(code: string | null | undefined): OverallBlockStatus {
  return isInactiveOverallBlock(code) ? 'inactive' : 'active'
}
